import { defineComponent, h, reactive } from 'vue';
import { QIcon } from 'quasar';
import utils from '../../Shared/utils.jsx';
import methods from '../../Shared/methods';
import filters from '../../Shared/filters';
import computed from '../../Shared/computed';

//const contentDef = {
export default defineComponent({
    name: 'ag-content-def',
    components: { QIcon },
    emits: ['async-done'],
    data: () => ({
        _uid: utils._uid_counter++,
        name: 'AgContentDef',
        Input: {},
        rowData: null,

        root: null,
        type: null,
        rowDataArg: null,
        contentDefs: null,
        addrowdata: null,
        scopeitems: null,
        controlscope: null,
        loading: null,

        c_context: null,
        _children: [],
        // Bumped on AG Grid refresh so Vue re-renders when $$additional_row_data
        // mutates on the same row object reference (no destroy/recreate).
        renderToken: 0,
    }),
    props: {
    },
    computed: {
        ...computed,
        rowIndex: function () {
            return this.params.node.rowIndex;
        },
    },
    methods: {
        ...methods,
        ...filters,
        applyCellParams(params) {
            // Do not assign this.params — AG Grid exposes it as a read-only prop.
            this.rowData = params.data;
            this.root = params.callcorp.root;
            this.type = params.callcorp.type;
            this.rowDataArg = params.callcorp.rowDataArg;
            this.contentDefs = params.callcorp.contentDefs;
            this.addrowdata = params.callcorp.addrowdata;
            this.scopeitems = params.callcorp.scopeitems;
            this.controlscope = params.callcorp.controlscope;
            this.c_context = params.callcorp.context;
            this.loading = params.callcorp.loading;
        },
        /**
         * AG Grid Vue: return true to keep this renderer instance across refreshCells.
         * AsyncLookup settlement used to force-refresh the whole row; without refresh(),
         * that destroyed open BasicPopupMenu state (All Users account-circle first click).
         */
        refresh(params) {
            this.applyCellParams(params);
            // Action-only columns (empty field) host popups/buttons — re-rendering them
            // remounts children and closes menus even when this instance is reused.
            if (params.colDef?.field)
                this.renderToken++;
            return true;
        },
        getAdditionalRowData(name) {
            if ('$$additional_row_data' in this.rowData)
                return name ? this.rowData.$$additional_row_data[name] : this.rowData.$$additional_row_data;
            else
                return '';
        },
        handleGridExportText(val) {
            if(this.rowData) {
                if(!this.rowData.exportValues)
                    this.rowData.exportValues = {};
                
                if(!this.rowData.exportValues[this.params.callcorp.field])
                    this.rowData.exportValues[this.params.callcorp.field] = [];
                
                this.rowData.exportValues[this.params.callcorp.field].push(val);
            }
        },
        finishRenderHandler(component) {
            if (component && component.$.uid !== this.$.uid) {
                if (!this._children.some(c => c.$.uid === component.$.uid))
                    this._children.push(component);
            }
        },
        /** After each AsyncLookup settles: repaint data cells; call grid asyncDone once when all lookups for this row are done (row-based progress). */
        notifyAdditionalRowAsyncFetchSettled(remainingRef) {
            try {
                if (this.params.api && this.params.node) {
                    // Skip ActionOnlyColumn cells (empty field in BaseAgGridMixin). Force-refreshing
                    // those remounts BasicPopupMenu and eats the first profile-icon click.
                    const columns = [];
                    for (const col of this.params.api.getColumns() || []) {
                        const def = col.getColDef();
                        if (!def?.cellRendererParams?.callcorp?.contentDefs)
                            continue;
                        if (!def.field)
                            continue;
                        columns.push(col.getColId());
                    }
                    // An empty list must not fall through to a whole-row refresh.
                    // That remounts action cells when the lookup only feeds those columns.
                    if (columns.length)
                        this.params.api.refreshCells({
                            rowNodes: [this.params.node],
                            columns,
                            force: true,
                        });
                }
            }
            catch (e) { /* ignore */ }
            remainingRef.n--;
            if (remainingRef.n <= 0 && typeof this.params.callcorp.asyncDone === 'function')
                this.params.callcorp.asyncDone({ rowIndex: this.params.node?.rowIndex, rowData: this.params.node?.data });
        },
    },
    created() {
        this.applyCellParams(this.params);

        // Add additional_row_data if not already there (first time for each row, we'll compute the AdditionalRowData, but then it is saved on the row).
        // reactive() so action-cell computeds (DisplayExpression / todisplay) update when an AsyncLookup
        // key is replaced, without refreshCells on the ActionOnlyColumn (that remounts open popups).
        if (this.params.callcorp.addrowdata && this.rowData && !('$$additional_row_data' in this.rowData)) {
            this.rowData.$$additional_row_data = reactive({});

            let asyncLookupCount = 0;
            for (let j = 0; j < this.params.callcorp.addrowdata.length; j++) {
                const add = this.params.callcorp.addrowdata[j];
                if (add.initValue)
                    this.rowData.$$additional_row_data[add.name] = utils.evaluateObject(add.initValue, this);

                for (let i = 0; i < add.contentDefs.length; i++) {
                    const c = add.contentDefs[i];
                    if (c.displayexpr && !utils.evaluate(c.displayexpr, this))
                        continue;

                    switch (c.type) {
                        case 'RawInterpolated':
                            this.rowData.$$additional_row_data[add.name] = utils.evaluateObject(c.valueexpr, this);
                            break;
                        case 'AsyncLookup':
                            asyncLookupCount++;
                            break;
                    }
                }
            }

            const asyncRemaining = { n: asyncLookupCount };

            for (let j = 0; j < this.params.callcorp.addrowdata.length; j++) {
                const add = this.params.callcorp.addrowdata[j];
                for (let i = 0; i < add.contentDefs.length; i++) {
                    const c = add.contentDefs[i];
                    if (c.displayexpr && !utils.evaluate(c.displayexpr, this))
                        continue;

                    if (c.type !== 'AsyncLookup')
                        continue;

                    this.rowData.$$additional_row_data[add.name] = {};
                    let url;
                    try {
                        url = utils.evaluate(c.urlexpr, this);
                    }
                    catch (err) {
                        console.error('Inside AgContentDef (urlexpr)', err);
                        this.notifyAdditionalRowAsyncFetchSettled(asyncRemaining);
                        continue;
                    }

                    try {
                        utils.api.get(url, false, c.cache).then(function (res) {
                            try {
                                this.Input = { Data: res };
                                this.rowData.$$additional_row_data[add.name] = utils.evaluateObject(c.valueexpr, this);
                            }
                            catch (err) {
                                console.error('Inside AgContentDef (valueexpr)', err);
                            }
                            this.notifyAdditionalRowAsyncFetchSettled(asyncRemaining);
                        }.bind(this))
                            .catch(function (err) {
                                console.error('Inside AgContentDef (api.get)', err);
                                this.notifyAdditionalRowAsyncFetchSettled(asyncRemaining);
                            }.bind(this));
                    }
                    catch (err) {
                        console.error('Inside AgContentDef (get)', err);
                        this.notifyAdditionalRowAsyncFetchSettled(asyncRemaining);
                    }
                }
            }
        }
    },
    render() {
        // Depend on renderToken so AsyncLookup-driven refresh() repaints cells that
        // read $$additional_row_data without destroying interactive children.
        void this.renderToken;

        const elements = [];

        if (this.loading && this.rowData === undefined) {
            return <div><div class="mdi-animate-spin" style="display: inline-block"><QIcon name="mdi-loading" /></div>One moment please…</div>;
        }

        for (let k = 0; k < this.params.callcorp.contentDefs.length; k++) {
            const content = this.params.callcorp.contentDefs[k];

            if (!content.Controls) continue;

            if (content.displayExpression)
                if (!utils.evaluate(content.displayExpression, this)) continue;

            for (let j = 0; j < content.Controls.length; j++) {
                const control = content.Controls[j];

                let DynamicControl = utils.getDynamicComponent(h, control);
                if (!DynamicControl) {
                    elements.push(<span>Control type of {control.ControlType} is unknown</span>);
                    continue;
                }

                elements.push(
                    <DynamicControl
                        key={control.$objectId}
                        id={`ag-content-id-${j}_${control.$objectId}`}
                        onFinishedRender={(reference) => this.finishRenderHandler(reference)}
                        type={control.ControlType}
                        name={control.ControlData.Name}
                        root={this.params.callcorp.root}
                        controlData={control.ControlData}
                        accessPermission={control.AccessPermission}
                        parentType="ContentDef"
                        rowData={() => this.rowData}
                        controlURL={control.ControlURL}
                        controlName={control.Name}
                        scopeitems={this.params.callcorp.scopeitems}
                        on-grid-exported-text={this.handleGridExportText}
                        controlscope={this.params.callcorp.controlscope}
                        controlEvents={control.Events}>
                    </DynamicControl>
                );
            }
        }

        return <div class={{ 'row': true, 'no-wrap': true, 'items-center': true, 'c-AgContentDef': true, [`c-name-${this.name || 'unnamed'}`]: true }} style="flex-wrap: nowrap; align-items: center;">{elements}</div>;
    }
});

//export default contentDef;